import { useMemo, useState } from "preact/hooks";
import { PHRASES } from "../content/phrases";
import { TWISTERS } from "../content/twisters";
import type { Card, FluencyCategoryId, Lang } from "../content/types";
import { dayStr } from "../lib/date";
import { profile } from "../state/store";
import { Chip } from "../components";
import { CardRow } from "./library/CardRow";
import { CardSheet } from "./library/CardSheet";
import { CATEGORY_LABEL, cardMatchesQuery, cardStatus, type CardStatus } from "./library/utils";

const ALL_CARDS: Card[] = [...TWISTERS, ...PHRASES];
const LEVELS = [1, 2, 3] as const;
const CATEGORIES = Object.keys(CATEGORY_LABEL) as FluencyCategoryId[];
const STATUSES: CardStatus[] = ["due", "learning", "cooling", "mastered", "untouched"];
const PAGE_SIZE = 40;

type KindFilter = "all" | "twister" | "phrase";
type LangFilter = "all" | Lang;

export function Library() {
  const p = profile.value;
  const today = dayStr();

  const [query, setQuery] = useState("");
  const [lang, setLang] = useState<LangFilter>("all");
  const [kind, setKind] = useState<KindFilter>("all");
  const [level, setLevel] = useState<number | null>(null);
  const [category, setCategory] = useState<FluencyCategoryId | null>(null);
  const [status, setStatus] = useState<CardStatus | null>(null);
  const [phoneme, setPhoneme] = useState<string | null>(null);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [openCard, setOpenCard] = useState<Card | null>(null);

  const filtered = useMemo(() => {
    return ALL_CARDS.filter((c) => {
      if (lang !== "all" && c.lang !== lang) return false;
      if (kind !== "all" && c.kind !== kind) return false;
      if (level !== null && c.level !== level) return false;
      if (category !== null && c.category !== category) return false;
      if (phoneme !== null && !c.phonemes.includes(phoneme)) return false;
      if (status !== null && cardStatus(c.id, p, today) !== status) return false;
      if (!cardMatchesQuery(c, query)) return false;
      return true;
    });
  }, [p, today, query, lang, kind, level, category, status, phoneme]);

  const shown = filtered.slice(0, visible);

  return (
    <div class="screen screen-library">
      <p class="display screen-title">Library</p>

      <input
        class="library-search mono"
        type="search"
        placeholder="Search twisters and phrases…"
        value={query}
        onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
      />

      <div class="library-filters">
        <div class="library-filter-row">
          {(["all", "en", "sv"] as LangFilter[]).map((l) => (
            <Chip key={l} active={lang === l} onClick={() => setLang(l)}>
              {l === "all" ? "Both languages" : l === "en" ? "English" : "Svenska"}
            </Chip>
          ))}
        </div>
        <div class="library-filter-row">
          {(["all", "twister", "phrase"] as KindFilter[]).map((k) => (
            <Chip key={k} active={kind === k} onClick={() => setKind(k)}>
              {k === "all" ? "Twisters + Phrases" : k === "twister" ? "Twisters" : "Phrases"}
            </Chip>
          ))}
        </div>
        <div class="library-filter-row">
          <Chip active={status === null} onClick={() => setStatus(null)}>
            Any status
          </Chip>
          {STATUSES.map((s) => (
            <Chip key={s} active={status === s} onClick={() => setStatus(status === s ? null : s)}>
              {s}
            </Chip>
          ))}
        </div>
        {kind !== "phrase" && (
          <div class="library-filter-row">
            <Chip active={level === null} onClick={() => setLevel(null)}>
              Any level
            </Chip>
            {LEVELS.map((lv) => (
              <Chip key={lv} active={level === lv} onClick={() => setLevel(level === lv ? null : lv)}>
                Level {lv}
              </Chip>
            ))}
          </div>
        )}
        {kind !== "twister" && (
          <div class="library-filter-row">
            <Chip active={category === null} onClick={() => setCategory(null)}>
              Any category
            </Chip>
            {CATEGORIES.map((c) => (
              <Chip key={c} active={category === c} onClick={() => setCategory(category === c ? null : c)}>
                {CATEGORY_LABEL[c]}
              </Chip>
            ))}
          </div>
        )}
        {phoneme && (
          <div class="library-filter-row">
            <Chip active onClick={() => setPhoneme(null)}>
              Sound: {phoneme} ✕
            </Chip>
          </div>
        )}
      </div>

      <p class="label library-count">
        {filtered.length} card{filtered.length === 1 ? "" : "s"}
      </p>

      <div class="library-list">
        {shown.map((card) => (
          <CardRow key={card.id} card={card} profile={p} today={today} onOpen={setOpenCard} onPhonemeClick={setPhoneme} />
        ))}
      </div>

      {visible < filtered.length && (
        <button class="library-load-more mono" type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
          Show {Math.min(PAGE_SIZE, filtered.length - visible)} more
        </button>
      )}

      <CardSheet card={openCard} profile={p} today={today} onClose={() => setOpenCard(null)} />
    </div>
  );
}
